import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  FileEdit, 
  Settings, 
  LogOut,
  GripVertical,
  Plus,
  Trash2,
  Save,
  ExternalLink,
  Loader2,
  Eye,
  Lock,
  Zap,
  Copy,
  AlertTriangle,
  Brain,
  MoreVertical,
  RefreshCw,
  Layers,
  Users,
  Palette,
  Edit2,
  CreditCard,
  CalendarClock,
  Sparkles,
  Target,
  CheckSquare,
  Link2,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  MessageSquare,
  Upload,
  BarChart3,
  Clock
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Logo from "../components/Logo";
import AppSidebar from "@/components/AppSidebar";
import FormBrandingSettings from "../components/FormBrandingSettings";
import FormAnalyticsDashboard from "../components/FormAnalyticsDashboard";
import UsageWarningBanner from "@/components/UsageWarningBanner";
import AIFormOptimizeModal from "@/components/AIFormOptimizeModal";
import AIFormBuilderWizard from "@/components/AIFormBuilderWizard";
import OptionsEditor from "@/components/OptionsEditor";
import SortableFieldItem from "@/components/SortableFieldItem";
import RichTextEditor from "@/components/RichTextEditor";
import SafeRichText from "@/components/SafeRichText";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";

const fieldTypes = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone Number" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date Picker" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown Select" },
  { value: "radio", label: "Radio Buttons" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "file_upload", label: "File Upload" }
];



export default function FormBuilderPage() {
  const { user, token, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const { formId: routeFormId } = useParams();
  const navigate = useNavigate();
  const isEditorMode = Boolean(routeFormId);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [formConfig, setFormConfig] = useState({
    fields: [],
    intro_text: "",
    thank_you_message: ""
  });
  // (was: const [draggedIndex, setDraggedIndex] = useState(null); — replaced by @dnd-kit)
  const [showNewFormDialog, setShowNewFormDialog] = useState(false);
  const [newFormName, setNewFormName] = useState("");
  const [newFormTemplate, setNewFormTemplate] = useState("project_based");
  const [newFormPurpose, setNewFormPurpose] = useState("pre_booking"); // Inquiry (pre_booking) or Client Intake (post_booking)
  const [creatingForm, setCreatingForm] = useState(false);
  const [scoringCriticalFields, setScoringCriticalFields] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, fieldIndex: null, fieldLabel: "" });
  const [formTemplates, setFormTemplates] = useState([]);
  const [deleteFormConfirmation, setDeleteFormConfirmation] = useState({ open: false, formId: null, formName: "" });
  const [changeTemplateDialog, setChangeTemplateDialog] = useState({ open: false, formId: null, currentTemplate: "", formMode: "" });
  const [selectedNewTemplate, setSelectedNewTemplate] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return t === "branding" || t === "analytics" ? t : "fields";
  });
  
  // Rename form state
  const [renameFormDialog, setRenameFormDialog] = useState({ open: false, formId: null, currentName: "" });
  const [newFormNameForRename, setNewFormNameForRename] = useState("");
  
  // AI optimization preset tracking
  const [appliedPreset, setAppliedPreset] = useState(null);
  
  // Document requirements state (for post_booking forms)
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [newDocLabel, setNewDocLabel] = useState("");
  const [newDocRequired, setNewDocRequired] = useState(true);
  const [documentMethod, setDocumentMethod] = useState("none"); // Business setting
  
  // Agreement requirement state (for post_booking forms)
  const [agreementRequired, setAgreementRequired] = useState(false);
  
  // AI Form Builder state
  const [showAIOptimizeModal, setShowAIOptimizeModal] = useState(false);
  const [showAIBuilderWizard, setShowAIBuilderWizard] = useState(false);
  const [businessAgreementUrl, setBusinessAgreementUrl] = useState(null); // From business settings
  
  // Scheduling link state (for pre_booking forms)
  const [schedulingLinks, setSchedulingLinks] = useState({});
  const [showSchedulingLink, setShowSchedulingLink] = useState(false);
  const [schedulingButtonText, setSchedulingButtonText] = useState("Schedule a Call");
  
  // Consent checkbox state (for pre_booking forms)
  const [consentCheckboxEnabled, setConsentCheckboxEnabled] = useState(false);
  const [consentCheckboxText, setConsentCheckboxText] = useState("I agree to be contacted regarding my request");
  const [consentCheckboxRequired, setConsentCheckboxRequired] = useState(false);
  
  // Guided setup state
  const [showGuidedSetup, setShowGuidedSetup] = useState(false);
  const [guidedStep, setGuidedStep] = useState(null); // null = modal, 'fields'|'thankyou'|'scheduling'|'documents'|'branding'|'share'|'save_changes'|'test_lead'
  
  // Scheduling link inline drawer
  const [showSchedulingDrawer, setShowSchedulingDrawer] = useState(false);
  const [drawerCalendly, setDrawerCalendly] = useState("");
  const [drawerGoogleCal, setDrawerGoogleCal] = useState("");
  const [drawerSquare, setDrawerSquare] = useState("");
  
  // Use subscription context instead of local state
  const { subscriptionStatus } = useSubscription();

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // "saved" | "unsaved" | "saving" | "failed"
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  
  // Branding state (lifted from FormBrandingSettings for unified save)
  const [branding, setBranding] = useState(null);
  const [brandingEnabled, setBrandingEnabled] = useState(true);
  
  // Inline form name editing
  const [editingFormName, setEditingFormName] = useState(false);
  const [inlineFormName, setInlineFormName] = useState("");
  
  // Duplicate form state
  const [duplicatingForm, setDuplicatingForm] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchUsage();
    fetchTemplates();
    fetchAgreementSettings();
    fetchSchedulingLinks();
  }, []);

  // Auto-select form from route param
  useEffect(() => {
    if (routeFormId && forms.length > 0 && selectedFormId !== routeFormId) {
      const form = forms.find(f => f.id === routeFormId);
      if (form) {
        selectForm(form);
      }
    }
  }, [routeFormId, forms]);

  // Mark dirty helper — call this from any user-initiated change
  const markDirty = () => {
    if (isEditorMode && selectedFormId) {
      setIsDirty(true);
      setSaveState("unsaved");
    }
  };

  // Browser beforeunload protection
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Navigation guard helper
  const navigateWithGuard = (path) => {
    if (isDirty) {
      pendingNavigationRef.current = path;
      setShowLeaveConfirm(true);
    } else {
      navigate(path);
    }
  };

  // Guided setup: scroll to section when step changes
  useEffect(() => {
    if (!guidedStep) return;
    if (guidedStep === 'branding') {
      setActiveTab('branding');
      return;
    }
    if (guidedStep === 'save_changes') {
      if (activeTab !== 'fields') setActiveTab('fields');
      setTimeout(() => {
        document.querySelector('[data-testid="save-form-btn"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return;
    }
    if (guidedStep === 'test_lead') return; // No scroll needed
    if (activeTab !== 'fields') setActiveTab('fields');
    const sectionMap = {
      fields: 'fields-section',
      thankyou: 'thankyou-section',
      scheduling: 'scheduling-section',
      documents: 'documents-section',
      share: 'share-section'
    };
    const sectionId = sectionMap[guidedStep];
    if (sectionId) {
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
    if (guidedStep === 'scheduling' && !hasSchedulingLink()) {
      setShowSchedulingDrawer(true);
      setDrawerCalendly(schedulingLinks.calendly || '');
      setDrawerGoogleCal(schedulingLinks.google_calendar || '');
      setDrawerSquare(schedulingLinks.square_appointments || '');
    }
  }, [guidedStep]);


  const fetchSchedulingLinks = async () => {
    try {
      // Read from integration_settings (where Settings page saves scheduling links)
      const response = await axios.get(`${API}/workflow/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const settings = response.data || {};
      
      // Convert to the format expected by the component
      // Priority: scheduling_link > calendar_link > square_link
      const links = {
        calendly: settings.scheduling_link || null,
        google_calendar: settings.calendar_link || null,
        square_appointments: settings.square_link || null
      };
      
      console.log("Scheduling URL from integration_settings:", settings.scheduling_link);
      setSchedulingLinks(links);
    } catch (error) {
      console.error("Failed to fetch scheduling links:", error);
      // Fallback to legacy endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/business/scheduling-links`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSchedulingLinks(fallbackResponse.data || {});
      } catch (fallbackError) {
        console.error("Fallback also failed");
      }
    }
  };
  
  // Get the first available scheduling link
  const getFirstSchedulingLink = () => {
    const links = schedulingLinks;
    // Priority matches integration_settings: scheduling_link (calendly) > calendar_link > square_link
    if (links.calendly) return links.calendly;
    if (links.google_calendar) return links.google_calendar;
    if (links.square_appointments) return links.square_appointments;
    return null;
  };
  
  // Check if any scheduling link is configured
  const hasSchedulingLink = () => {
    return !!getFirstSchedulingLink();
  };

  const fetchAgreementSettings = async () => {
    try {
      const response = await axios.get(`${API}/business/agreement-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Check if agreement is configured (enabled with valid content)
      const settings = response.data || {};
      const isConfigured = settings.enabled && (
        (settings.mode === "link" && settings.agreement_url) ||
        (settings.mode === "text" && settings.agreement_text)
      );
      setBusinessAgreementUrl(isConfigured ? (settings.agreement_url || "configured") : null);
    } catch (error) {
      console.error("Failed to fetch agreement settings");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/form-templates`);
      setFormTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/business/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch usage");
    }
  };

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API}/business/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForms(response.data.forms || []);
      
      // Check for form ID in URL query params
      const formIdFromUrl = searchParams.get('form');
      
      if (response.data.forms && response.data.forms.length > 0) {
        // If form ID is in URL, select that form; otherwise select first form
        const targetForm = formIdFromUrl 
          ? response.data.forms.find(f => f.id === formIdFromUrl) 
          : null;
        
        const formToSelect = targetForm || response.data.forms[0];
        setSelectedFormId(formToSelect.id);
        setScoringCriticalFields(formToSelect.scoring_critical_fields || []);
        setDocumentRequirements(formToSelect.document_requirements || []);
        setAgreementRequired(formToSelect.agreement_required || false);
        // Load scheduling link settings for pre_booking forms
        setShowSchedulingLink(formToSelect.show_scheduling_link || false);
        setSchedulingButtonText(formToSelect.scheduling_button_text || "Schedule a Call");
        // Load consent checkbox settings
        setConsentCheckboxEnabled(formToSelect.consent_checkbox_enabled || false);
        setConsentCheckboxText(formToSelect.consent_checkbox_text || "I agree to be contacted regarding my request");
        setConsentCheckboxRequired(formToSelect.consent_checkbox_required || false);
        setBrandingEnabled(formToSelect.branding_enabled !== false);
        setFormConfig({
          fields: withStableUids(formToSelect.fields),
          intro_text: formToSelect.intro_text || "",
          thank_you_message: formToSelect.thank_you_message || ""
        });
      }
      
      // Fetch document handling settings
      fetchDocumentMethod();
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch forms");
      // Fall back to legacy form config
      fetchFormConfig();
    }
  };
  
  const fetchDocumentMethod = async () => {
    try {
      const response = await axios.get(`${API}/business/document-handling`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentMethod(response.data.method || "none");
    } catch (error) {
      console.error("Failed to fetch document handling settings");
    }
  };

  const selectForm = (form) => {
    setSelectedFormId(form.id);
    setScoringCriticalFields(form.scoring_critical_fields || []);
    setDocumentRequirements(form.document_requirements || []);
    setAgreementRequired(form.agreement_required || false);
    setShowSchedulingLink(form.show_scheduling_link || false);
    setSchedulingButtonText(form.scheduling_button_text || "Schedule a Call");
    setConsentCheckboxEnabled(form.consent_checkbox_enabled || false);
    setConsentCheckboxText(form.consent_checkbox_text || "I agree to be contacted regarding my request");
    setConsentCheckboxRequired(form.consent_checkbox_required || false);
    setInlineFormName(form.name || "");
    
    // Reset branding state — will be fetched fresh by FormBrandingSettings
    setBranding(null);
    setBrandingEnabled(form.branding_enabled !== false);
    
    const inferredMode = form.mode || 
      ((form.document_requirements?.length > 0 || form.agreement_required) ? "post_booking" : "pre_booking");
    
    setFormConfig({
      fields: withStableUids(form.fields),
      intro_text: form.intro_text || "",
      thank_you_message: form.thank_you_message || "",
      mode: inferredMode
    });
    
    setIsDirty(false);
    setSaveState("saved");
  };

  const createNewForm = async () => {
    if (!newFormName.trim()) {
      toast.error("Please enter a form name");
      return;
    }
    
    // Check forms limit before creating
    if (usage && forms.length >= (usage.forms_limit || 1)) {
      toast.error(`You've reached your plan's limit of ${usage.forms_limit || 1} form(s). Upgrade to create more.`);
      return;
    }
    
    setCreatingForm(true);
    try {
      const response = await axios.post(`${API}/business/forms`, 
        { 
          name: newFormName, 
          template_category: newFormTemplate,
          mode: newFormPurpose  // pre_booking or post_booking
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Form created successfully!");
      setShowNewFormDialog(false);
      setNewFormName("");
      setNewFormTemplate("project_based");
      setNewFormPurpose("pre_booking");
      
      // Refresh forms and navigate to the new form's editor
      await fetchForms();
      if (response.data.form) {
        const newForm = response.data.form;
        navigate(`/form-builder/${newForm.id}`);
        // Trigger guided setup for newly created form
        const formId = newForm.id;
        const guidedKey = `guided_setup_started_form_${formId}`;
        if (!localStorage.getItem(guidedKey)) {
          localStorage.setItem(guidedKey, 'true');
          setShowGuidedSetup(true);
          setGuidedStep(null);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to create form";
      toast.error(message);
    } finally {
      setCreatingForm(false);
    }
  };

  const deleteForm = async (formId) => {
    try {
      await axios.delete(`${API}/business/forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Form deleted");
      await fetchForms();
      // Select first remaining form or clear selection
      if (forms.length > 1) {
        const remainingForms = forms.filter(f => f.id !== formId);
        if (remainingForms.length > 0) {
          selectForm(remainingForms[0]);
        }
      } else {
        setSelectedFormId(null);
        setFormConfig({ fields: [], intro_text: "", thank_you_message: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete form");
    }
  };

  const changeFormTemplate = async (formId, newTemplate) => {
    try {
      // Get new template fields
      const templatesRes = await axios.get(`${API}/form-templates`);
      const template = templatesRes.data.templates.find(t => t.id === newTemplate);
      
      await axios.put(`${API}/business/forms/${formId}`, {
        template_category: newTemplate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Template changed to ${template?.name || newTemplate}`);
      await fetchForms();
    } catch (error) {
      toast.error("Failed to change template");
    }
  };

  const renameForm = async (formId, newName) => {
    try {
      await axios.put(`${API}/business/forms/${formId}`, {
        name: newName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Form renamed successfully");
      await fetchForms();
      setRenameFormDialog({ open: false, formId: null, currentName: "" });
      setNewFormNameForRename("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename form");
    }
  };

  const duplicateForm = async (form) => {
    if (usage && forms.length >= (usage.forms_limit || 1)) {
      toast.error(`Form limit reached. Upgrade to create more.`);
      return;
    }
    setDuplicatingForm(form.id);
    try {
      const response = await axios.post(`${API}/business/forms`, {
        name: `${form.name} (Copy)`,
        template_category: form.template_category || "project_based",
        mode: form.mode || "pre_booking"
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Copy fields from original form to the new one
      if (response.data.form) {
        await axios.put(`${API}/business/forms/${response.data.form.id}`, {
          fields: form.fields || [],
          intro_text: form.intro_text || "",
          thank_you_message: form.thank_you_message || "",
          show_scheduling_link: form.show_scheduling_link || false,
          consent_checkbox_enabled: form.consent_checkbox_enabled || false,
          consent_checkbox_text: form.consent_checkbox_text || "",
          consent_checkbox_required: form.consent_checkbox_required || false
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      toast.success("Form duplicated!");
      await fetchForms();
    } catch (error) {
      toast.error("Failed to duplicate form");
    } finally {
      setDuplicatingForm(null);
    }
  };

  // Inline form name save (for editor header)
  const saveInlineFormName = async () => {
    if (!inlineFormName.trim() || !selectedFormId) return;
    setEditingFormName(false);
    try {
      await axios.put(`${API}/business/forms/${selectedFormId}`, { name: inlineFormName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchForms();
    } catch (error) {
      toast.error("Failed to rename form");
    }
  };

  const resetFormToTemplate = async (formId, templateCategory) => {
    try {
      // This will reset the form fields to the template defaults
      const response = await axios.post(`${API}/business/forms/${formId}/reset-to-template`, {
        template_category: templateCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Form reset to template defaults");
      await fetchForms();
    } catch (error) {
      // If reset endpoint doesn't exist, just change template
      await changeFormTemplate(formId, templateCategory);
    }
  };

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${API}/business/form-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormConfig({ ...response.data.form_config, fields: withStableUids(response.data.form_config?.fields) });
    } catch (error) {
      toast.error("Failed to load form configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveFormConfig = async () => {
    setSaving(true);
    setSaveState("saving");
    // Snapshot current state for rollback
    const snapshot = {
      formConfig: { ...formConfig },
      showSchedulingLink,
      schedulingButtonText,
      consentCheckboxEnabled,
      consentCheckboxText,
      consentCheckboxRequired,
      documentRequirements: [...documentRequirements],
      agreementRequired,
      branding: branding ? { ...branding } : null
    };
    try {
      const currentForm = forms.find(f => f.id === selectedFormId);
      const isPostBooking = currentForm?.mode === "post_booking";
      const isPreBooking = currentForm?.mode === "pre_booking";
      
      const updateData = {
        fields: stripUids(formConfig.fields),
        intro_text: formConfig.intro_text,
        thank_you_message: formConfig.thank_you_message
      };
      
      // Include document requirements and agreement for post_booking forms
      if (isPostBooking) {
        updateData.document_requirements = documentRequirements;
        updateData.agreement_required = agreementRequired;
      }
      
      // Include scheduling link settings for pre_booking forms
      if (isPreBooking) {
        updateData.show_scheduling_link = showSchedulingLink;
        updateData.scheduling_button_text = schedulingButtonText;
        updateData.consent_checkbox_enabled = consentCheckboxEnabled;
        updateData.consent_checkbox_text = consentCheckboxText;
        updateData.consent_checkbox_required = consentCheckboxRequired;
      }
      
      // Include branding if it has been loaded/modified
      if (branding) {
        updateData.branding = branding;
      }
      updateData.branding_enabled = brandingEnabled;
      
      if (selectedFormId) {
        // Save to new forms API
        await axios.put(`${API}/business/forms/${selectedFormId}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Fall back to legacy API
        await axios.put(`${API}/business/form-config`, formConfig, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success("Form saved!");
      setIsDirty(false);
      setSaveState("saved");
      
      // If guided setup was waiting for save, advance to test_lead step
      if (guidedStep === 'save_changes') {
        setGuidedStep('test_lead');
      }
    } catch (error) {
      // Revert to snapshot on failure
      setFormConfig(snapshot.formConfig);
      setShowSchedulingLink(snapshot.showSchedulingLink);
      setSchedulingButtonText(snapshot.schedulingButtonText);
      setConsentCheckboxEnabled(snapshot.consentCheckboxEnabled);
      setConsentCheckboxText(snapshot.consentCheckboxText);
      setConsentCheckboxRequired(snapshot.consentCheckboxRequired);
      setDocumentRequirements(snapshot.documentRequirements);
      setAgreementRequired(snapshot.agreementRequired);
      if (snapshot.branding) setBranding(snapshot.branding);
      toast.error("Couldn't save changes");
      setSaveState("failed");
    } finally {
      setSaving(false);
    }
  };
  
  // Document requirement functions
  const addDocumentRequirement = () => {
    if (!newDocLabel.trim()) {
      toast.error("Please enter a document name");
      return;
    }
    if (documentRequirements.length >= 25) {
      toast.error("Maximum 25 document requirements allowed");
      return;
    }
    
    const newDoc = {
      id: `doc_${crypto.randomUUID().substring(0, 8)}`,
      label: newDocLabel.trim(),
      required: newDocRequired
    };
    
    setDocumentRequirements([...documentRequirements, newDoc]);
    setNewDocLabel("");
    setNewDocRequired(true);
  };
  
  const removeDocumentRequirement = (docId) => {
    setDocumentRequirements(documentRequirements.filter(d => d.id !== docId));
  };
  
  const toggleDocRequired = (docId) => {
    setDocumentRequirements(documentRequirements.map(d => 
      d.id === docId ? { ...d, required: !d.required } : d
    ));
  };

  const [newlyAddedFieldId, setNewlyAddedFieldId] = useState(null);

  const addField = () => {
    const newFieldId = crypto.randomUUID();
    const newField = {
      id: newFieldId,
      _uid: newFieldId,
      field_type: "text",
      label: "New Field",
      placeholder: "",
      required: true,
      options: null,
      order: formConfig.fields.length
    };
    setFormConfig({
      ...formConfig,
      fields: [...formConfig.fields, newField]
    });
    setNewlyAddedFieldId(newFieldId);
    
    // Scroll to the new field after render
    setTimeout(() => {
      const newFieldElement = document.querySelector(`[data-field-id="${newFieldId}"]`);
      if (newFieldElement) {
        newFieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => setNewlyAddedFieldId(null), 2000);
    }, 100);
    markDirty();
  };

  const updateField = (index, updates) => {
    const newFields = [...formConfig.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFormConfig({ ...formConfig, fields: newFields });
    markDirty();
  };

  // Keywords that indicate a field is critical for AI scoring
  const SCORING_CRITICAL_KEYWORDS = [
    'budget', 'price', 'cost', 'spend', 'investment',
    'timeline', 'deadline', 'when', 'start date', 'timeframe',
    'decision', 'authority', 'role', 'title', 'position',
    'problem', 'challenge', 'need', 'goal', 'objective',
    'urgency', 'urgent', 'priority', 'asap',
    'company', 'organization', 'business'
  ];

  const isFieldScoringCritical = (fieldLabel) => {
    // Handle both 'label' and 'name' field properties
    if (!fieldLabel) return false;
    const labelLower = fieldLabel.toLowerCase();
    
    // Check against backend-provided critical fields
    const matchesBackendList = scoringCriticalFields.some(
      criticalLabel => labelLower.includes(criticalLabel.toLowerCase()) ||
                       criticalLabel.toLowerCase().includes(labelLower)
    );
    
    // Also check against our keyword list for broader coverage
    const matchesKeyword = SCORING_CRITICAL_KEYWORDS.some(
      keyword => labelLower.includes(keyword)
    );
    
    return matchesBackendList || matchesKeyword;
  };

  const handleDeleteClick = (index) => {
    const field = formConfig.fields[index];
    const fieldLabel = field.label || field.name;
    // Only show scoring confirmation for pre_booking forms
    if (formConfig.mode !== "post_booking" && isFieldScoringCritical(fieldLabel)) {
      // Show confirmation dialog for scoring-critical fields
      setDeleteConfirmation({ open: true, fieldIndex: index, fieldLabel: fieldLabel });
    } else {
      // Delete immediately for non-critical fields or post_booking forms
      removeField(index);
    }
  };

  const confirmDelete = () => {
    if (deleteConfirmation.fieldIndex !== null) {
      removeField(deleteConfirmation.fieldIndex);
    }
    setDeleteConfirmation({ open: false, fieldIndex: null, fieldLabel: "" });
  };

  const removeField = (index) => {
    const newFields = formConfig.fields.filter((_, i) => i !== index);
    newFields.forEach((f, i) => f.order = i);
    setFormConfig({ ...formConfig, fields: newFields });
    markDirty();
  };

  // Ensure each field has a stable client-side id for @dnd-kit (server returns
  // fields without ids). Idempotent: existing _uid is preserved.
  const withStableUids = (fields) =>
    (fields || []).map((f) => ({
      ...f,
      _uid:
        f._uid ||
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `f_${Math.random().toString(36).slice(2)}_${Date.now()}`),
    }));

  // Strip client-only fields before sending to backend.
  // eslint-disable-next-line no-unused-vars
  const stripUids = (fields) => (fields || []).map(({ _uid, ...rest }) => rest);

  // Use Mouse + Touch + Keyboard sensors. PointerSensor has known issues
  // with synthetic events in some browsers; Mouse+Touch covers all real users.
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeDragId, setActiveDragId] = useState(null);
  const [overDragId, setOverDragId] = useState(null);
  const [overPosition, setOverPosition] = useState(null); // 'before' | 'after'

  const handleDndDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDndDragOver = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setOverDragId(null);
      setOverPosition(null);
      return;
    }
    setOverDragId(over.id);
    const fromIdx = formConfig.fields.findIndex((f) => f._uid === active.id);
    const toIdx = formConfig.fields.findIndex((f) => f._uid === over.id);
    setOverPosition(toIdx < fromIdx ? "before" : "after");
  };

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    setOverDragId(null);
    setOverPosition(null);
    if (!over || active.id === over.id) return;

    const oldIndex = formConfig.fields.findIndex((f) => f._uid === active.id);
    const newIndex = formConfig.fields.findIndex((f) => f._uid === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(formConfig.fields, oldIndex, newIndex).map(
      (f, i) => ({ ...f, order: i }),
    );
    setFormConfig({ ...formConfig, fields: reordered });
    markDirty();
  };

  const handleDndDragCancel = () => {
    setActiveDragId(null);
    setOverDragId(null);
    setOverPosition(null);
  };

  // Get form URL from selected form or fall back to user's form_slug
  const selectedForm = forms.find(f => f.id === selectedFormId);
  const formUrl = selectedForm 
    ? `${window.location.origin}/form/${selectedForm.slug}` 
    : (user ? `${window.location.origin}/form/${user.form_slug}` : "");

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex" style={{ fontFamily: 'Inter, sans-serif' }}>
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6">
        <div className={isEditorMode ? "max-w-4xl mx-auto" : "max-w-5xl mx-auto"}>
          <UsageWarningBanner />
          
          {/* ===== MANAGEMENT VIEW (no formId in URL) ===== */}
          {!isEditorMode && (
            <>
              {/* Page Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Forms</h1>
                  <p className="text-muted-foreground mt-1">
                    Create and manage your lead and intake forms.
                    {forms.length > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground/70">
                        {forms.length} of {usage?.forms_limit === -1 ? '∞' : (usage?.forms_limit || 1)} used
                      </span>
                    )}
                  </p>
                </div>
                <Dialog open={showNewFormDialog} onOpenChange={setShowNewFormDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      className="rounded-full"
                      disabled={usage && usage.forms_limit !== -1 && forms.length >= (usage.forms_limit || 1)}
                      data-testid="create-form-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Form
                    </Button>
                  </DialogTrigger>
                  {/* New Form Dialog Content — reused below */}
                  <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                      <DialogTitle>Create New Form</DialogTitle>
                      <DialogDescription>Choose a type and template for your form.</DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-5 overflow-y-auto flex-1 min-h-0 pr-1">
                      <div>
                        <Label htmlFor="new-form-name">Form Name</Label>
                        <Input id="new-form-name" placeholder="e.g., Project Lead Form" value={newFormName} onChange={(e) => setNewFormName(e.target.value)} className="mt-2" data-testid="new-form-name-input" />
                      </div>
                      <div>
                        <Label>Form Purpose</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${newFormPurpose === "pre_booking" ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setNewFormPurpose("pre_booking")} data-testid="purpose-inquiry">
                            <p className="font-medium text-sm">Lead Form</p>
                            <p className="text-xs text-muted-foreground mt-1">Lead capture & qualification</p>
                          </div>
                          <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${newFormPurpose === "post_booking" ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setNewFormPurpose("post_booking")} data-testid="purpose-intake">
                            <p className="font-medium text-sm">Intake Form</p>
                            <p className="text-xs text-muted-foreground mt-1">Client onboarding</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label>Form Template</Label>
                        <p className="text-xs text-muted-foreground mt-1 mb-3">Determines AI scoring criteria</p>
                        <div className="grid grid-cols-2 gap-2">
                          {formTemplates.filter(t => newFormPurpose === "post_booking" ? t.id === "project_based" : true).map((template) => (
                            <div key={template.id} onClick={() => setNewFormTemplate(template.id)} className={`p-3 border-2 rounded-xl cursor-pointer transition-all ${newFormTemplate === template.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`template-${template.id}`}>
                              <p className="font-medium text-sm">{template.name}</p>
                              <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="flex flex-col sm:flex-row gap-2 flex-shrink-0 border-t pt-4">
                      {newFormPurpose === "pre_booking" && (
                        <Button type="button" variant="outline" onClick={() => { setShowNewFormDialog(false); setShowAIBuilderWizard(true); }} className="mr-auto border-purple-200 hover:bg-purple-50">
                          <Sparkles className="w-4 h-4 mr-2 text-purple-600" /> Build with AI
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => { setShowNewFormDialog(false); setNewFormTemplate("project_based"); setNewFormPurpose("pre_booking"); }}>Cancel</Button>
                      <Button onClick={createNewForm} disabled={creatingForm || !newFormName.trim()} data-testid="confirm-create-form-btn">
                        {creatingForm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                        Create Form
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Forms Grid */}
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : forms.length === 0 ? (
                <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <FileEdit className="w-16 h-16 text-muted-foreground/20 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No forms yet</h3>
                    <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">Create your first form to start collecting leads and onboarding clients.</p>
                    <Button onClick={() => setShowNewFormDialog(true)} className="rounded-full" data-testid="create-first-form-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Form
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {forms.map((form) => {
                    const formMode = form.mode || ((form.document_requirements?.length > 0 || form.agreement_required) ? "post_booking" : "pre_booking");
                    const createdAt = form.created_at ? new Date(form.created_at) : null;
                    const lastSub = form.last_submission_at ? new Date(form.last_submission_at) : null;
                    const recentCount = form.submissions_last_7_days || 0;
                    const isActive = recentCount > 0;
                    const formSlug = form.slug || form.id;
                    const formLink = `${window.location.origin}/form/${formSlug}`;
                    
                    // Format relative time
                    const timeAgo = (date) => {
                      if (!date) return null;
                      const now = new Date();
                      const diffMs = now - date;
                      const diffMins = Math.floor(diffMs / 60000);
                      if (diffMins < 1) return "Just now";
                      if (diffMins < 60) return `${diffMins}m ago`;
                      const diffHrs = Math.floor(diffMins / 60);
                      if (diffHrs < 24) return `${diffHrs}h ago`;
                      const diffDays = Math.floor(diffHrs / 24);
                      if (diffDays < 7) return `${diffDays}d ago`;
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    };
                    
                    return (
                      <Card 
                        key={form.id} 
                        className="rounded-2xl border border-border/30 shadow-[0_2px_8px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_28px_rgb(0,0,0,0.08)] hover:border-border/60 hover:scale-[1.01] transition-all duration-200 cursor-pointer group flex flex-col"
                        onClick={() => navigate(`/form-builder/${form.id}`)}
                        data-testid={`form-card-${form.id}`}
                      >
                        <CardContent className="p-5 flex flex-col flex-1">
                          {/* Top: Name + Menu */}
                          <div className="flex items-start justify-between mb-2.5">
                            <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2 pr-2">{form.name}</h3>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 -mt-0.5" data-testid={`form-menu-${form.id}`}>
                                  <MoreVertical className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem onClick={() => { setRenameFormDialog({ open: true, formId: form.id, currentName: form.name }); setNewFormNameForRename(form.name); }}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => duplicateForm(form)} disabled={duplicatingForm === form.id}>
                                  <Copy className="w-4 h-4 mr-2" /> {duplicatingForm === form.id ? "Duplicating..." : "Duplicate"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setDeleteFormConfirmation({ open: true, formId: form.id, formName: form.name })} className="text-destructive focus:text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          {/* Badge */}
                          <div className="mb-3">
                            <Badge className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 ${formMode === "pre_booking" ? "bg-blue-50 text-blue-600 border-blue-200/60" : "bg-emerald-50 text-emerald-600 border-emerald-200/60"}`} variant="outline">
                              {formMode === "pre_booking" ? "Lead" : "Intake"}
                            </Badge>
                          </div>
                          
                          {/* Stats — 3 tight lines */}
                          <div className="space-y-0.5 text-[11px] text-muted-foreground/80 mb-4 flex-1">
                            <p>{form.fields?.length || 0} fields · Created: {createdAt ? createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : "—"}</p>
                            <p>{recentCount} submission{recentCount !== 1 ? "s" : ""} · Last 7 days</p>
                            <p>{lastSub ? `Last: ${timeAgo(lastSub)}` : "No submissions yet"}</p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-3 border-t border-border/30" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" className="rounded-full flex-1 h-8 text-xs font-medium" onClick={() => navigate(`/form-builder/${form.id}`)} data-testid={`edit-form-${form.id}`}>
                              <Edit2 className="w-3 h-3 mr-1.5" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs text-muted-foreground hover:text-foreground" onClick={() => { navigator.clipboard.writeText(formLink); toast.success("Link copied!"); }} data-testid={`copy-link-${form.id}`}>
                              <Copy className="w-3 h-3 mr-1.5" /> Copy Link
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ===== EDITOR VIEW (formId in URL) ===== */}
          {isEditorMode && (
            <>
              {/* Sticky Editor Header */}
              <div className="sticky top-0 z-40 bg-zinc-50/95 backdrop-blur-sm -mx-6 px-6 py-3 mb-6 border-b border-border/40">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                  {/* Left: Back + Form Name */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <button 
                      onClick={() => navigateWithGuard('/form-builder')} 
                      className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 text-sm flex-shrink-0"
                      data-testid="back-to-forms-btn"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Forms</span>
                    </button>
                    <span className="text-muted-foreground/40">/</span>
                    {editingFormName ? (
                      <input
                        autoFocus
                        value={inlineFormName}
                        onChange={(e) => setInlineFormName(e.target.value)}
                        onBlur={saveInlineFormName}
                        onKeyDown={(e) => { if (e.key === "Enter") saveInlineFormName(); if (e.key === "Escape") setEditingFormName(false); }}
                        className="text-lg font-semibold bg-transparent border-b-2 border-primary outline-none px-1 min-w-[120px] max-w-[300px]"
                        data-testid="inline-form-name-input"
                      />
                    ) : (
                      <button 
                        onClick={() => { setInlineFormName(selectedForm?.name || ""); setEditingFormName(true); }}
                        className="text-lg font-semibold text-foreground hover:text-primary transition-colors truncate max-w-[300px]"
                        data-testid="form-name-display"
                      >
                        {selectedForm?.name || "Loading..."}
                      </button>
                    )}
                  </div>
                  
                  {/* Center: Save State */}
                  <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground mx-4">
                    {saveState === "saved" && !isDirty && <><Check className="w-3.5 h-3.5 text-green-500" /> All changes saved</>}
                    {saveState === "unsaved" && isDirty && <><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> Unsaved changes</>}
                    {saveState === "saving" && <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>}
                    {saveState === "failed" && <><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Failed to save</>}
                  </div>
                  
                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" asChild className="rounded-full" size="sm" data-testid="preview-form-btn">
                      <a href={formUrl} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4 mr-1.5" />
                        Preview
                      </a>
                    </Button>
                    <Button 
                      onClick={saveFormConfig} 
                      disabled={saving || !selectedFormId || !isDirty} 
                      className={`rounded-full transition-all ${guidedStep === 'save_changes' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}
                      size="sm"
                      data-testid="save-form-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>

              {/* Form Link (in editor) */}
              {selectedForm && (
                <div id="share-section" className={`flex items-center gap-2 p-2 bg-muted/50 rounded-lg w-fit mb-6 transition-all ${guidedStep === 'share' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Form Link:</span>
                  <a href={formUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="form-url-link">
                    {formUrl.length > 50 ? `${formUrl.slice(0, 50)}...` : formUrl}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(formUrl); toast.success("Link copied to clipboard!"); }} data-testid="copy-form-url-btn">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                  <TabsList className="bg-muted/50 p-1 rounded-full inline-flex">
                    <TabsTrigger 
                      value="fields" 
                      className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
                      data-testid="tab-fields"
                    >
                      <FileEdit className="w-4 h-4 mr-2" />
                      Fields
                    </TabsTrigger>
                    <TabsTrigger 
                      value="branding" 
                      className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
                      data-testid="tab-branding"
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Branding
                    </TabsTrigger>
                    <TabsTrigger 
                      value="analytics" 
                      className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
                      data-testid="tab-analytics"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Analytics
                    </TabsTrigger>
                  </TabsList>

                  {/* Fields Tab */}
                  <TabsContent value="fields" className="space-y-6">
                  {/* Intro Text */}
                  <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                    <CardHeader>
                      <CardTitle >Form Introduction</CardTitle>
                      <CardDescription>This text appears at the top of your intake form. Use the toolbar for headings, alignment, lists, and emphasis.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <RichTextEditor
                        value={formConfig.intro_text || ""}
                        onChange={(html) => { setFormConfig({ ...formConfig, intro_text: html }); markDirty(); }}
                        placeholder="Welcome message for your customers..."
                        testId="intro-text-input"
                        enableHeadings
                        enableOrderedList
                        enableAlignment
                        enableLinks={false}
                        fontFamily={branding?.font_family || "default"}
                      />
                </CardContent>
              </Card>

              {/* AI Scoring Panel - Only for pre_booking (Lead) forms */}
              {formConfig.mode !== "post_booking" && (
                <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-purple-600" />
                          <span className="font-medium text-purple-900">AI Scoring</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Total Weight: </span>
                            <span className={`font-bold ${
                              (() => {
                                const total = formConfig.fields
                                  .filter(f => f.include_in_score)
                                  .reduce((sum, f) => sum + (f.score_weight || 0), 0);
                                return total === 100 ? 'text-green-600' : total > 0 ? 'text-red-600' : 'text-muted-foreground';
                              })()
                            }`}>
                              {formConfig.fields.filter(f => f.include_in_score).reduce((sum, f) => sum + (f.score_weight || 0), 0)}%
                              {formConfig.fields.filter(f => f.include_in_score).reduce((sum, f) => sum + (f.score_weight || 0), 0) === 100 && 
                                <span className="ml-1 text-green-600">✓</span>
                              }
                            </span>
                          </div>
                          <div className="border-l pl-4">
                            <span className="text-muted-foreground">Fields: </span>
                            <span className="font-bold">
                              {formConfig.fields.filter(f => f.include_in_score).length}
                              <span className="text-muted-foreground font-normal">/6 max</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Applied preset indicator */}
                      {appliedPreset && (
                        <div className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-purple-800">
                            AI optimized for: <strong>{appliedPreset}</strong>
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAIOptimizeModal(true)}
                            className="ml-auto text-purple-700 hover:text-purple-800 hover:bg-purple-100 h-6 text-xs"
                          >
                            Re-optimize
                          </Button>
                        </div>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowAIOptimizeModal(true)}
                        className="bg-white hover:bg-purple-50 border-purple-200"
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                        {appliedPreset ? "Re-optimize with AI" : "Optimize with AI"}
                      </Button>
                    </div>
                    
                    {/* Scoring fields breakdown */}
                    {formConfig.fields.filter(f => f.include_in_score).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-200/50">
                        <p className="text-xs text-purple-700 mb-2">Scoring Breakdown:</p>
                        <div className="flex flex-wrap gap-2">
                          {formConfig.fields
                            .filter(f => f.include_in_score)
                            .map((field, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className="bg-white text-purple-800 border-purple-200"
                              >
                                {field.label}: {field.score_weight}%
                              </Badge>
                            ))
                          }
                        </div>
                      </div>
                    )}
                    
                    {/* Validation warnings */}
                    {(() => {
                      const scoringFields = formConfig.fields.filter(f => f.include_in_score);
                      const totalWeight = scoringFields.reduce((sum, f) => sum + (f.score_weight || 0), 0);
                      if (scoringFields.length > 0 && totalWeight !== 100) {
                        return (
                          <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-xs text-red-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Scoring weights must total 100% (currently {totalWeight}%)
                            </p>
                          </div>
                        );
                      }
                      if (scoringFields.length > 6) {
                        return (
                          <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-xs text-red-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Maximum 6 scoring fields allowed ({scoringFields.length} selected)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Form Fields */}
              <Card id="fields-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'fields' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle >Form Fields</CardTitle>
                    <CardDescription>
                      {formConfig.mode === "post_booking" 
                        ? "Drag to reorder. Click to edit. Required fields affect intake completeness detection."
                        : <>Drag to reorder. Click to edit. <span className="inline-flex items-center gap-1"><Brain className="w-3 h-3 text-primary" /> AI Scoring</span> fields help determine lead quality.</>
                      }
                    </CardDescription>
                  </div>
                  <Button onClick={addField} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm" data-testid="add-field-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Field
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  <TooltipProvider>
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragStart={handleDndDragStart}
                    onDragOver={handleDndDragOver}
                    onDragEnd={handleDndDragEnd}
                    onDragCancel={handleDndDragCancel}
                    autoScroll={{ threshold: { x: 0, y: 0.2 }, acceleration: 12 }}
                  >
                    <SortableContext
                      items={formConfig.fields.map((f) => f._uid)}
                      strategy={verticalListSortingStrategy}
                    >
                  {formConfig.fields.map((field, index) => {
                    const fieldLabel = field.label || field.name;
                    const fieldType = field.field_type || field.type;
                    // Single source of truth: show scoring UI only when toggle is ON
                    const isCritical = field.include_in_score === true;
                    return (
                    <SortableFieldItem
                      key={field._uid}
                      id={field._uid}
                      index={index}
                      isCritical={isCritical}
                      isHighlighted={newlyAddedFieldId === field.id}
                      testId={`field-item-${index}`}
                      isOver={overDragId === field._uid && activeDragId !== field._uid}
                      insertPosition={overDragId === field._uid ? overPosition : null}
                    >
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label>Field Label</Label>
                                {isCritical && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30 gap-1">
                                        <Brain className="w-3 h-3" />
                                        AI Scoring
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[250px]">
                                      <p>This field is used by AI to calculate lead scores. Removing it may reduce scoring accuracy.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <Input
                                value={fieldLabel}
                                onChange={(e) => updateField(index, { label: e.target.value, name: e.target.value })}
                                placeholder="Field label"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Field Type</Label>
                              <Select
                                value={fieldType}
                                onValueChange={(value) => updateField(index, { 
                                  field_type: value,
                                  type: value,
                                  options: ["select", "radio", "checkbox"].includes(value) ? ["Option 1", "Option 2"] : null
                                })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {!["select", "radio", "checkbox"].includes(fieldType) && (
                            <div className="space-y-2">
                              <Label>Placeholder</Label>
                              <Input
                                value={field.placeholder || ""}
                                onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                placeholder="Placeholder text"
                              />
                            </div>
                          )}
                          
                          {["select", "radio", "checkbox"].includes(fieldType) && (
                            <OptionsEditor
                              field={field}
                              index={index}
                              updateField={updateField}
                            />
                          )}
                          
                          {/* File Upload Config */}
                          {fieldType === "file_upload" && (
                            <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm">Allow multiple files</Label>
                                <Switch
                                  checked={field.multiple_files || false}
                                  onCheckedChange={(checked) => updateField(index, { multiple_files: checked })}
                                  data-testid={`file-multiple-${index}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Accepted types</Label>
                                <p className="text-xs text-muted-foreground">PDF, DOCX, TXT, PNG, JPG (max 5MB per file)</p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={field.required}
                                onCheckedChange={(checked) => updateField(index, { required: checked })}
                              />
                              <Label className="cursor-pointer">Required</Label>
                            </div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteClick(index)}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  data-testid={`delete-field-${index}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              {isCritical && (
                                <TooltipContent>
                                  <p>Deleting this field may impact lead scoring</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </div>
                          
                          {/* AI Scoring Controls - Only for pre_booking (Lead) forms */}
                          {formConfig.mode !== "post_booking" && (
                            <div className="pt-3 mt-3 border-t border-dashed">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={field.include_in_score === true}
                                    onCheckedChange={(checked) => {
                                      updateField(index, { 
                                        include_in_score: checked,
                                        score_weight: checked ? (field.score_weight || 20) : 0
                                      });
                                    }}
                                    data-testid={`scoring-toggle-${index}`}
                                  />
                                  <Label className="cursor-pointer text-sm flex items-center gap-1">
                                    <Target className="w-3 h-3" />
                                    Include in AI Scoring
                                  </Label>
                                </div>
                                {field.include_in_score && (
                                  <div className="flex items-center gap-2">
                                    <Label className="text-xs text-muted-foreground">Weight:</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      max="100"
                                      value={field.score_weight || 0}
                                      onChange={(e) => {
                                        const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                        updateField(index, { score_weight: val });
                                      }}
                                      className="w-16 h-7 text-sm"
                                      data-testid={`scoring-weight-${index}`}
                                    />
                                    <span className="text-xs text-muted-foreground">%</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                    </SortableFieldItem>
                  )})}
                    </SortableContext>
                  </DndContext>
                  </TooltipProvider>
                  
                  {formConfig.fields.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileEdit className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No fields yet. Click "Add Field" to get started.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Thank You Message */}
              <Card id="thankyou-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'thankyou' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                <CardHeader>
                  <CardTitle >Thank You Message</CardTitle>
                  <CardDescription>Shown after a successful submission. Use the toolbar for headings, alignment, lists, and emphasis.</CardDescription>
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={formConfig.thank_you_message || ""}
                    onChange={(html) => { setFormConfig({ ...formConfig, thank_you_message: html }); markDirty(); }}
                    placeholder="Thank you for your submission..."
                    testId="thank-you-message-input"
                    enableHeadings
                    enableOrderedList
                    enableAlignment
                    enableLinks={false}
                    fontFamily={branding?.font_family || "default"}
                  />
                </CardContent>
              </Card>
              
              {/* Scheduling Link (for pre_booking / Lead forms only) */}
              {(() => {
                const currentForm = forms.find(f => f.id === selectedFormId);
                const firstLink = getFirstSchedulingLink();
                return currentForm?.mode === "pre_booking" && (
                  <Card id="scheduling-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'scheduling' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5" />
                        Scheduling Link
                      </CardTitle>
                      <CardDescription>
                        Add a scheduling button to the thank you page after submission.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Warning if no scheduling link configured */}
                      {!firstLink && (
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <strong>No scheduling link configured.</strong> Go to{" "}
                            <Link to="/settings" className="underline font-medium">Settings → Scheduling Links</Link>{" "}
                            to add your Calendly, Google Calendar, or other booking link first.
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="show-scheduling-link" className="text-base">
                            Show scheduling link after submission
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {firstLink ? "Uses: " + firstLink.substring(0, 40) + "..." : "Enable after adding a link in Settings"}
                          </p>
                        </div>
                        <Switch
                          id="show-scheduling-link"
                          checked={showSchedulingLink}
                          onCheckedChange={(v) => { setShowSchedulingLink(v); markDirty(); }}
                          disabled={!firstLink}
                          data-testid="show-scheduling-toggle"
                        />
                      </div>
                      
                      {showSchedulingLink && firstLink && (
                        <div className="space-y-2 pt-2 border-t">
                          <Label htmlFor="scheduling-btn-text">Button Text</Label>
                          <Input
                            id="scheduling-btn-text"
                            value={schedulingButtonText}
                            onChange={(e) => { setSchedulingButtonText(e.target.value); markDirty(); }}
                            placeholder="Schedule a Call"
                            maxLength={50}
                            data-testid="scheduling-button-text-input"
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
              
              {/* Consent Checkbox (for pre_booking forms only) */}
              {(() => {
                const currentForm = forms.find(f => f.id === selectedFormId);
                return currentForm?.mode === "pre_booking" && (
                  <Card id="consent-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'consent' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckSquare className="w-5 h-5" />
                        Consent
                      </CardTitle>
                      <CardDescription>
                        Add a consent checkbox before the submit button.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="consent-toggle" className="text-base">
                            Include consent checkbox
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Shown at the bottom of the form before submit
                          </p>
                        </div>
                        <Switch
                          id="consent-toggle"
                          checked={consentCheckboxEnabled}
                          onCheckedChange={(v) => { setConsentCheckboxEnabled(v); markDirty(); }}
                          data-testid="consent-toggle"
                        />
                      </div>
                      
                      {consentCheckboxEnabled && (
                        <div className="space-y-4 pt-2 border-t">
                          <div className="space-y-2">
                            <Label htmlFor="consent-text">Submission Terms body</Label>
                            <p className="text-xs text-muted-foreground">
                              The bullets and notes that appear above the checkbox. Use the toolbar for paragraphs, bullet points, bold/italic, and links. The checkbox itself reads <em>"I have read and agree to the Submission Terms."</em>
                            </p>
                            <RichTextEditor
                              value={consentCheckboxText}
                              onChange={(html) => { setConsentCheckboxText(html); markDirty(); }}
                              placeholder="• You are the creator of this work or have the rights to submit it"
                              testId="consent-text-input"
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label htmlFor="consent-required" className="text-sm">
                                Make required
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                If on, form cannot be submitted without checking
                              </p>
                            </div>
                            <Switch
                              id="consent-required"
                              checked={consentCheckboxRequired}
                              onCheckedChange={(v) => { setConsentCheckboxRequired(v); markDirty(); }}
                              data-testid="consent-required-toggle"
                            />
                          </div>
                          <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 px-6 py-8" data-testid="consent-preview">
                            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-4">
                              Preview · how applicants see it
                            </div>
                            <div
                              className="mx-auto max-w-[560px]"
                              style={{
                                fontFamily: {
                                  default: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                                  serif: "'Playfair Display', 'Libre Baskerville', Georgia, serif",
                                  modern_sans: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                                  elegant_sans: "'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif",
                                }[branding?.font_family || "default"],
                              }}
                            >
                              <h3 className="text-base font-medium text-zinc-500 mb-5">
                                Submission Terms
                              </h3>
                              <div
                                className="text-[13.5px] text-zinc-700 leading-[1.65] [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:marker:text-zinc-400 [&_p]:my-2 [&_a]:text-indigo-600 [&_a]:underline"
                              >
                                <SafeRichText
                                  html={consentCheckboxText || "<p>I agree to be contacted regarding my request</p>"}
                                />
                              </div>
                              <label className="mt-8 flex items-start gap-3 cursor-default select-none">
                                <input
                                  type="checkbox"
                                  disabled
                                  className="w-[18px] h-[18px] mt-0.5 rounded-[4px] border border-zinc-300 flex-shrink-0"
                                />
                                <span className="text-[14px] text-zinc-800 leading-snug">
                                  I have read and agree to the Submission Terms.
                                  {consentCheckboxRequired && <span className="text-zinc-400 ml-1">*</span>}
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
              
              {/* Document Requirements (for post_booking forms only) */}
              {(() => {
                const currentForm = forms.find(f => f.id === selectedFormId);
                return currentForm?.mode === "post_booking" && (
                  <Card id="documents-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'documents' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileEdit className="w-5 h-5" />
                        Required Documents
                      </CardTitle>
                      <CardDescription>
                        Define documents clients must confirm during intake. Clients will upload via your configured method.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Warning if document method is not set */}
                      {documentMethod === "none" && (
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-800">
                          <strong>Note:</strong> Document handling is not enabled. Go to{" "}
                          <Link to="/settings" className="underline font-medium">Settings → Document Handling</Link>{" "}
                          to configure how clients will submit documents.
                        </div>
                      )}
                      
                      {/* Current document requirements */}
                      {documentRequirements.length > 0 && (
                        <div className="space-y-2">
                          {documentRequirements.map((doc) => (
                            <div 
                              key={doc.id} 
                              className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                              data-testid={`doc-req-${doc.id}`}
                            >
                              <div className="flex-1">
                                <span className="font-medium">{doc.label}</span>
                              </div>
                              <button
                                onClick={() => toggleDocRequired(doc.id)}
                                className={`px-2 py-1 text-xs rounded ${
                                  doc.required 
                                    ? "bg-amber-100 text-amber-700" 
                                    : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                {doc.required ? "Required" : "Optional"}
                              </button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeDocumentRequirement(doc.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add new document requirement */}
                      <div className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                          <Label>Document Name</Label>
                          <Input
                            value={newDocLabel}
                            onChange={(e) => setNewDocLabel(e.target.value)}
                            placeholder="e.g., Signed Contract, ID Copy"
                            maxLength={80}
                            data-testid="new-doc-input"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={newDocRequired}
                              onChange={(e) => setNewDocRequired(e.target.checked)}
                              className="w-4 h-4"
                            />
                            Required
                          </label>
                          <Button
                            onClick={addDocumentRequirement}
                            variant="outline"
                            size="sm"
                            disabled={!newDocLabel.trim() || documentRequirements.length >= 25}
                            data-testid="add-doc-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground">
                        {documentRequirements.length}/25 documents • Clients will confirm uploads in the intake form
                      </p>
                    </CardContent>
                  </Card>
                );
              })()}
              
              {/* Agreement Requirement (for post_booking forms only) */}
              {(() => {
                const currentForm = forms.find(f => f.id === selectedFormId);
                return currentForm?.mode === "post_booking" && (
                  <Card id="agreement-section" className={`rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all ${guidedStep === 'documents' ? 'ring-2 ring-primary/40 ring-offset-2 animate-pulse' : ''}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Agreement Requirement
                      </CardTitle>
                      <CardDescription>
                        Require clients to accept your terms before submitting.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Agreement Configuration Status */}
                      {businessAgreementUrl ? (
                        // Agreement IS configured
                        <>
                          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm">Require agreement acknowledgment</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Clients must accept your agreement before submitting.
                              </p>
                            </div>
                            <Switch
                              checked={agreementRequired}
                              onCheckedChange={(v) => { setAgreementRequired(v); markDirty(); }}
                              data-testid="agreement-required-toggle"
                            />
                          </div>
                          
                          {agreementRequired && (
                            <div className="p-3 bg-green-50 border border-green-100 rounded-lg text-sm text-green-800">
                              <strong>Preview:</strong> Clients will see a checkbox: "I agree to the terms" 
                              with a link/modal to view your agreement.
                            </div>
                          )}
                          
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <span className="text-green-600">✓</span> Agreement configured in Settings
                          </p>
                        </>
                      ) : (
                        // Agreement NOT configured - show warning
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-800 mb-1">No agreement configured</p>
                              <p className="text-sm text-amber-700 mb-3">
                                To require clients to accept terms before submitting, you need to configure your agreement first.
                              </p>
                              <a 
                                href="/settings?tab=tools" 
                                className="inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:underline"
                              >
                                Go to Settings → Agreement
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
                  </TabsContent>

                  {/* Branding Tab */}
                  <TabsContent value="branding">
                    <FormBrandingSettings 
                      formId={selectedFormId}
                      token={token}
                      branding={branding}
                      onBrandingChange={(updated) => setBranding(updated)}
                      onDirty={markDirty}
                      brandingEnabled={brandingEnabled}
                      onBrandingEnabledChange={(val) => { setBrandingEnabled(val); markDirty(); }}
                      businessName={user?.business?.business_name || user?.business_name}
                    />
                  </TabsContent>

                  {/* Analytics Tab */}
                  <TabsContent value="analytics" className="space-y-6" data-testid="tab-analytics-content">
                    {selectedFormId ? (
                      <FormAnalyticsDashboard
                        formId={selectedFormId}
                        formName={forms.find(f => f.id === selectedFormId)?.name}
                      />
                    ) : (
                      <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
                        <CardContent className="p-8 text-center text-muted-foreground">
                          Select a form to see its analytics.
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </>
          )}
        </div>
      </main>

      {/* Scoring Field Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmation.open} onOpenChange={(open) => !open && setDeleteConfirmation({ open: false, fieldIndex: null, fieldLabel: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Delete AI Scoring Field?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                <strong>"{deleteConfirmation.fieldLabel}"</strong> is used by AI to calculate lead scores.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                <strong>Impact of removing this field:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Lead scores may be less accurate</li>
                  <li>AI will use default values for missing data</li>
                  <li>Priority recommendations may be affected</li>
                </ul>
              </div>
              <p className="text-sm">
                You can still delete this field if it doesn't apply to your business.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Field</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Delete Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Form Confirmation Dialog */}
      <AlertDialog open={deleteFormConfirmation.open} onOpenChange={(open) => !open && setDeleteFormConfirmation({ open: false, formId: null, formName: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete Form?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>"{deleteFormConfirmation.formName}"</strong>?
              </p>
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                <strong>Warning:</strong> This action cannot be undone. All leads associated with this form will remain in your inbox.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                deleteForm(deleteFormConfirmation.formId);
                setDeleteFormConfirmation({ open: false, formId: null, formName: "" });
              }}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-form-btn"
            >
              Delete Form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Template Dialog */}
      <Dialog open={changeTemplateDialog.open} onOpenChange={(open) => !open && setChangeTemplateDialog({ open: false, formId: null, currentTemplate: "", formMode: "" })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary" />
              Change Form Template
            </DialogTitle>
            <DialogDescription>
              Choose a new template type for this form. This will update the AI scoring criteria.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {/* Lead Forms Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Pre-Booking</Badge>
                <Label className="text-sm font-medium">Lead Forms</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Capture and qualify new prospects with AI lead scoring</p>
              <div className="grid grid-cols-2 gap-2">
                {formTemplates.filter(t => t.mode === "pre_booking").map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedNewTemplate(template.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNewTemplate === template.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    } ${changeTemplateDialog.currentTemplate === template.id ? 'ring-1 ring-muted-foreground/20' : ''}`}
                    data-testid={`change-to-template-${template.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{template.name}</p>
                      {changeTemplateDialog.currentTemplate === template.id && (
                        <Badge variant="secondary" className="text-xs">Current</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Separator */}
            <div className="border-t border-border" />
            
            {/* Client Intake Forms Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Post-Booking</Badge>
                <Label className="text-sm font-medium">Client Intake Forms</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Collect complete information after a client books or signs</p>
              <div className="grid grid-cols-2 gap-2">
                {formTemplates.filter(t => t.mode === "post_booking").map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedNewTemplate(template.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNewTemplate === template.id 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50'
                    } ${changeTemplateDialog.currentTemplate === template.id ? 'ring-1 ring-muted-foreground/20' : ''}`}
                    data-testid={`change-to-template-${template.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{template.name}</p>
                      {changeTemplateDialog.currentTemplate === template.id && (
                        <Badge variant="secondary" className="text-xs">Current</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {selectedNewTemplate !== changeTemplateDialog.currentTemplate && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Note:</strong> Changing the template will update the AI scoring criteria. Your existing form fields will remain unchanged.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeTemplateDialog({ open: false, formId: null, currentTemplate: "", formMode: "" })}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedNewTemplate !== changeTemplateDialog.currentTemplate) {
                  changeFormTemplate(changeTemplateDialog.formId, selectedNewTemplate);
                }
                setChangeTemplateDialog({ open: false, formId: null, currentTemplate: "", formMode: "" });
              }}
              disabled={selectedNewTemplate === changeTemplateDialog.currentTemplate}
              data-testid="confirm-change-template-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Change Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Form Dialog */}
      <Dialog open={renameFormDialog.open} onOpenChange={(open) => {
        if (!open) {
          setRenameFormDialog({ open: false, formId: null, currentName: "" });
          setNewFormNameForRename("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" />
              Rename Form
            </DialogTitle>
            <DialogDescription>
              Enter a new name for your form.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-form-input" className="mb-2 block">Form Name</Label>
            <Input
              id="rename-form-input"
              value={newFormNameForRename}
              onChange={(e) => setNewFormNameForRename(e.target.value)}
              placeholder="Enter form name"
              className="w-full"
              data-testid="rename-form-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newFormNameForRename.trim() && newFormNameForRename !== renameFormDialog.currentName) {
                  renameForm(renameFormDialog.formId, newFormNameForRename.trim());
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setRenameFormDialog({ open: false, formId: null, currentName: "" });
              setNewFormNameForRename("");
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => renameForm(renameFormDialog.formId, newFormNameForRename.trim())}
              disabled={!newFormNameForRename.trim() || newFormNameForRename === renameFormDialog.currentName}
              data-testid="confirm-rename-form-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Name
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Form Optimize Modal */}
      <AIFormOptimizeModal
        isOpen={showAIOptimizeModal}
        onClose={() => setShowAIOptimizeModal(false)}
        form={forms.find(f => f.id === selectedFormId)}
        token={token}
        onApplySuggestions={(suggestions, presetApplied) => {
          // Apply accepted suggestions to form fields
          const updatedFields = formConfig.fields.map(field => {
            const suggestion = suggestions.find(s => s.original_label === field.label);
            if (suggestion) {
              return {
                ...field,
                label: suggestion.suggested_label || field.label,
                include_in_score: suggestion.include_in_score,
                score_weight: suggestion.score_weight || 0
              };
            }
            return field;
          });
          setFormConfig({ ...formConfig, fields: updatedFields });
          
          // Track the applied preset for the indicator
          if (presetApplied) {
            // Fetch preset name from presets
            setAppliedPreset(presetApplied.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
          }
        }}
      />

      {/* AI Form Builder Wizard */}
      <AIFormBuilderWizard
        isOpen={showAIBuilderWizard}
        onClose={() => setShowAIBuilderWizard(false)}
        token={token}
        onCreateForm={async (formData) => {
          // Create the form with AI-generated fields
          setCreatingForm(true);
          try {
            const response = await axios.post(
              `${API}/business/forms`,
              {
                name: formData.name,
                template_category: "project_based",
                mode: "pre_booking",
                fields: formData.fields
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            await fetchForms();
            setSelectedFormId(response.data.id);
            await selectForm(response.data);
            toast.success("AI-generated form created successfully!");
            // Trigger guided setup for AI-created form
            const formId = response.data.id || response.data.form?.id;
            if (formId) {
              const guidedKey = `guided_setup_started_form_${formId}`;
              if (!localStorage.getItem(guidedKey)) {
                localStorage.setItem(guidedKey, 'true');
                setShowGuidedSetup(true);
                setGuidedStep(null);
              }
            }
          } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to create form");
          } finally {
            setCreatingForm(false);
          }
        }}
      />

      {/* Guided Setup Modal — shown once per form after creation */}
      <Dialog open={showGuidedSetup && guidedStep === null} onOpenChange={(open) => {
        if (!open) { setShowGuidedSetup(false); setGuidedStep(null); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Your form is ready — let's finish it
            </DialogTitle>
            <DialogDescription>
              Walk through a few quick steps to get the most out of your form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            {[
              { id: 'fields', icon: FileEdit, label: 'Edit your fields' },
              { id: 'thankyou', icon: MessageSquare, label: 'Add a thank you message' },
              ...(() => {
                const currentForm = forms.find(f => f.id === selectedFormId);
                if (currentForm?.mode === 'pre_booking') {
                  return [{ id: 'scheduling', icon: CalendarClock, label: 'Add scheduling link' }];
                }
                return [{ id: 'documents', icon: FileEdit, label: 'Add documents & agreement' }];
              })(),
              { id: 'branding', icon: Palette, label: 'Customize branding' },
              { id: 'share', icon: Link2, label: 'Share your form' },
              { id: 'save_changes', icon: Save, label: 'Save your changes' },
              { id: 'test_lead', icon: Eye, label: 'Send your first test lead' },
            ].map((step) => (
              <button
                key={step.id}
                onClick={() => { setGuidedStep(step.id); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left"
                data-testid={`guided-step-${step.id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <step.icon className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium">{step.label}</span>
                <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
              </button>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              className="w-full rounded-full"
              onClick={() => { setGuidedStep('fields'); }}
              data-testid="guided-continue-btn"
            >
              Continue setup
            </Button>
            <button
              onClick={() => {
                setShowGuidedSetup(false);
                setGuidedStep(null);
                if (selectedFormId) localStorage.setItem(`guided_setup_completed_form_${selectedFormId}`, 'true');
              }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="guided-skip-btn"
            >
              I'll do this later
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guided Step Bar — appears when walking through steps */}
      {guidedStep && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white/95 backdrop-blur-sm border border-border/60 shadow-[0_8px_32px_rgba(0,0,0,0.12)] rounded-2xl px-6 py-4 flex items-center gap-4 max-w-xl w-[90vw]" data-testid="guided-tooltip-bar">
          <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="flex-1 text-sm font-medium text-foreground">
            {guidedStep === 'fields' && "You can customize or remove fields anytime"}
            {guidedStep === 'thankyou' && "Customize what clients see after submitting"}
            {guidedStep === 'scheduling' && (hasSchedulingLink() ? "Toggle to show your scheduling link after submission" : "Add a scheduling link to let clients book time with you")}
            {guidedStep === 'documents' && "Set up required documents and agreement for intake"}
            {guidedStep === 'branding' && "Make the form match your brand"}
            {guidedStep === 'share' && "Copy your form link or preview it"}
            {guidedStep === 'save_changes' && "Click 'Save Changes' to apply your updates"}
            {guidedStep === 'test_lead' && "Submit a test entry to see how everything works"}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {guidedStep === 'test_lead' ? (
              <>
                <Button
                  size="sm"
                  className="rounded-full text-xs"
                  onClick={() => {
                    if (formUrl) {
                      window.open(formUrl, '_blank');
                    }
                    setGuidedStep(null);
                    setShowGuidedSetup(false);
                    if (selectedFormId) {
                      localStorage.setItem(`guided_setup_completed_form_${selectedFormId}`, 'true');
                    }
                  }}
                  data-testid="guided-open-form-btn"
                >
                  Open Form
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full text-xs"
                  onClick={() => {
                    if (formUrl) {
                      navigator.clipboard.writeText(formUrl);
                      toast.success("Link copied!");
                    }
                  }}
                  data-testid="guided-copy-link-btn"
                >
                  Copy Link
                </Button>
              </>
            ) : guidedStep !== 'save_changes' ? (
              <Button
                size="sm"
                className="rounded-full text-xs"
                onClick={() => {
                  const currentForm = forms.find(f => f.id === selectedFormId);
                  const isPreBooking = currentForm?.mode === 'pre_booking';
                  const steps = ['fields', 'thankyou', isPreBooking ? 'scheduling' : 'documents', 'branding', 'share', 'save_changes'];
                  const currentIdx = steps.indexOf(guidedStep);
                  if (currentIdx < steps.length - 1) {
                    const nextStep = steps[currentIdx + 1];
                    setGuidedStep(nextStep);
                    if (nextStep === 'branding') {
                      setActiveTab('branding');
                    } else if (nextStep === 'save_changes') {
                      if (activeTab !== 'fields') setActiveTab('fields');
                      setTimeout(() => {
                        document.querySelector('[data-testid="save-form-btn"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 150);
                    } else if (activeTab !== 'fields') {
                      setActiveTab('fields');
                    }
                    if (nextStep === 'scheduling' && !hasSchedulingLink()) {
                      setShowSchedulingDrawer(true);
                      setDrawerCalendly(schedulingLinks.calendly || '');
                      setDrawerGoogleCal(schedulingLinks.google_calendar || '');
                      setDrawerSquare(schedulingLinks.square_appointments || '');
                    }
                    const sectionId = { fields: 'fields-section', thankyou: 'thankyou-section', scheduling: 'scheduling-section', documents: 'documents-section', share: 'share-section' }[nextStep];
                    if (sectionId) {
                      setTimeout(() => {
                        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 150);
                    }
                  }
                }}
                data-testid="guided-next-btn"
              >
                Next <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            ) : null}
            <button
              onClick={() => {
                setGuidedStep(null);
                setShowGuidedSetup(false);
                if (selectedFormId) {
                  localStorage.setItem(`guided_setup_completed_form_${selectedFormId}`, 'true');
                }
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
              data-testid="guided-exit-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Inline Scheduling Link Drawer */}
      <Dialog open={showSchedulingDrawer} onOpenChange={setShowSchedulingDrawer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5" />
              Add Scheduling Link
            </DialogTitle>
            <DialogDescription>
              Add your booking link so clients can schedule time with you after submitting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="drawer-calendly">Calendly</Label>
              <Input
                id="drawer-calendly"
                value={drawerCalendly}
                onChange={(e) => setDrawerCalendly(e.target.value)}
                placeholder="https://calendly.com/your-link"
                data-testid="drawer-calendly-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="drawer-google-cal">Google Calendar</Label>
              <Input
                id="drawer-google-cal"
                value={drawerGoogleCal}
                onChange={(e) => setDrawerGoogleCal(e.target.value)}
                placeholder="https://calendar.google.com/..."
                data-testid="drawer-google-cal-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="drawer-square">Square Appointments</Label>
              <Input
                id="drawer-square"
                value={drawerSquare}
                onChange={(e) => setDrawerSquare(e.target.value)}
                placeholder="https://squareup.com/..."
                data-testid="drawer-square-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSchedulingDrawer(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              className="rounded-full"
              onClick={async () => {
                try {
                  await axios.put(`${API}/workflow/settings`, {
                    scheduling_link: drawerCalendly || null,
                    calendar_link: drawerGoogleCal || null,
                    square_link: drawerSquare || null
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  setSchedulingLinks({
                    calendly: drawerCalendly || null,
                    google_calendar: drawerGoogleCal || null,
                    square_appointments: drawerSquare || null
                  });
                  toast.success("Scheduling link saved!");
                  setShowSchedulingDrawer(false);
                } catch (error) {
                  toast.error("Failed to save scheduling link");
                }
              }}
              data-testid="drawer-save-scheduling-btn"
            >
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Navigation Guard — Leave Confirmation */}
      <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              Leave without saving? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowLeaveConfirm(false); pendingNavigationRef.current = null; }}>
              Stay
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setIsDirty(false);
              setShowLeaveConfirm(false);
              const path = pendingNavigationRef.current;
              pendingNavigationRef.current = null;
              if (path) navigate(path);
            }}>
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
